export type BlogSection = {
  h: string;
  p: string[];
  list?: string[];
};

export type BlogPost = {
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  date: string;
  readMinutes: number;
  sections: BlogSection[];
  takeaways: string[];
};

export const BLOG_CATEGORIES = [
  "Pricing",
  "Preparation",
  "Shipping Types",
  "Seasonal",
  "Specialty Vehicles",
  "Buying & Selling",
  "Fraud Prevention",
  "Insurance & Claims",
] as const;

export const BLOG_POSTS: BlogPost[] = [
  {
    slug: "naples-car-shipping-rankings-investigation",
    title:
      "Who Decided These Are the “Best Car Shipping Companies in Naples”? Our Investigation Into the FreightWaves Rankings",
    excerpt:
      "A national publication ranked the “best” car shipping companies in Naples — yet many of its top picks aren't in Naples, or even Florida. We dug into the methodology, the review scores and the advertiser disclosure. Here is what we found, and the questions consumers should ask.",
    category: "Fraud Prevention",
    date: "2026-08-29",
    readMinutes: 14,
    sections: [
      {
        h: "Some aren't in Naples — and that raises bigger questions",
        p: [
          "We started looking into this because of one simple fact: we know the Naples auto transport market. Yet when FreightWaves published its August 2026 article titled “Best Car Shipping Companies in Naples, FL,” many of the companies presented to Naples consumers weren't actually located in Naples. Some aren't even particularly close. Some are national brokers headquartered in entirely different states.",
          "Since beginning this investigation, we have also been told something potentially even more concerning: that some companies operating in the American auto transport industry may have substantial portions of their sales, dispatch or customer-service operations outside the United States, despite presenting themselves to American consumers as U.S. companies.",
          "We want to be extremely clear: we have NOT independently confirmed that allegation regarding every company on FreightWaves' list, and we are not presenting it as fact. In fact, some of the companies we checked clearly have U.S. corporate registrations and U.S. addresses. But the allegation raises a question any publication recommending companies to consumers should be able to answer: where are these companies actually operating from?",
        ],
        list: [
          "Where are the people answering American consumers' phones?",
          "Where are the salespeople negotiating these transportation transactions?",
          "Where are dispatchers located? Where is customer service located?",
          "Where are consumers' personal information and credit-card information being handled?",
        ],
      },
      {
        h: "A U.S. address doesn't necessarily tell consumers where the operation is",
        p: [
          "A company can be legally incorporated in the United States and maintain a legitimate U.S. mailing or business address while employing workers or contractors throughout the world. There is nothing inherently illegal about outsourcing — thousands of American companies do it.",
          "But if a publication is going to recommend a business to consumers as one of the “Best Car Shipping Companies in Naples,” consumers deserve more transparency about what that means. Is it a locally owned Naples company employing people in Naples? A Florida company? A U.S. national company? A U.S.-registered brokerage whose sales operation is substantially overseas? Those are very different business models — and consumers generally aren't told the difference.",
        ],
      },
      {
        h: "The same three companies top every “local” page",
        p: [
          "FreightWaves says it evaluated more than 2,400 companies nationwide and scored providers based on cost, service options, availability and customer reviews. Yet the same three companies appear prominently across geographically different FreightWaves pages: Nexus Auto Transport (4.8/5), Sherpa Auto Transport (4.8/5) and Navi Auto Transport (4.4/5).",
          "Those same companies aren't merely recommended in Naples. FreightWaves also places the same three at the top of its Cape Coral page, and the same three appear at the top of route-specific pages such as Orlando-to-Seattle. So how “local” are these local rankings? If essentially the same national companies repeatedly occupy the premium positions regardless of which city the consumer searches, what exactly makes the Naples ranking a Naples ranking?",
        ],
      },
      {
        h: "Where the recommended companies are actually based",
        p: [
          "Nexus Auto Transport identifies its business address as 1320 Tower Road, Schaumburg, Illinois 60173. That's Illinois — not Naples, not Southwest Florida, not even Florida. Sherpa Auto Transport is associated with Charlotte, North Carolina. Again: not Naples.",
          "Navi Auto Transport's website describes the company as headquartered at 250 N Sunny Slope Rd., Suite 200, Brookfield, Wisconsin 53005. The BBB also lists a second location at 16192 Coastal Highway, Lewes, Delaware 19958, and Navi's own legal terms identify the underlying company as Navi Transport Services LLC, a Delaware limited liability company. We found evidence of a U.S. corporate entity and U.S. addresses for Navi, and we are not alleging that Navi isn't a U.S. company.",
          "But Navi's LinkedIn company page identifies employees with names including Nikolay Andonov, Ivan Karakostov, Gregori Stoilov and Emil Atanasov. That doesn't establish where anyone works, and a person's name obviously tells us nothing about citizenship or location. But it demonstrates why the investigation has to go deeper than an LLC address. The relevant question isn't someone's nationality — it's where the work is actually being performed.",
        ],
      },
      {
        h: "Compare that with the actual Southwest Florida companies",
        p: [
          "Now compare those national businesses with companies FreightWaves lists farther down its Naples article. Shaughnessy Overland Express is actually located in Naples. Towmax Transport Corp. is located in Cape Coral. Friendly Auto Transport and Young's Auto Transport are associated with Fort Myers. At least those companies have an identifiable Southwest Florida connection.",
          "But Cape Coral still isn't Naples. Fort Myers still isn't Naples. And Illinois, North Carolina, Wisconsin and Delaware certainly aren't Naples. So what does it mean when a consumer is told these are the “Best Car Shipping Companies in Naples”?",
          "And here is something that should make Naples consumers pause: Enterprise Auto Transport is an owner-operated, federally licensed auto transport broker with offices in Naples, Florida and Minnesota, a 15+ year track record, A+ BBB standing, USDOT #2273104 and MC #774106. We are not on FreightWaves' list at all. If the article truly identified the best car shipping companies in Naples, it is difficult to understand how a local, licensed, established Naples broker — with a real Minnesota office as well — could be entirely omitted while national companies headquartered elsewhere were included.",
        ],
      },
      {
        h: "The consumer-review data doesn't match the scores",
        p: [
          "FreightWaves gives Nexus 4.8/5, Sherpa 4.8/5 and Navi 4.4/5. But the public consumer-review data doesn't always line up neatly with those scores. The most dramatic example is Navi: the BBB currently reports 1.91/5 based on 110 customer reviews, along with 53 complaints during the previous three years. Meanwhile, FreightWaves scores it 4.4/5. That is an enormous difference.",
          "And then there is Trustpilot, which currently displays a notice indicating it has taken action involving reviews on Navi's profile, including removing reviews it identified as fake. That isn't our accusation — that's information being displayed by the review platform itself. So how does FreightWaves arrive at 4.4/5? We would like to see the calculation.",
          "The comparison we find particularly difficult to understand: FreightWaves includes Shaughnessy Overland Express, which is actually located in Naples, and itself reports approximately 4.8/5 on Google, 4.3/5 on Yelp and an A+ BBB rating — yet gives Shaughnessy an overall score of approximately 4.12/5, lower than Navi's 4.4/5 despite Navi's 1.91/5 BBB showing. Maybe there is a perfectly reasonable explanation. Then explain it.",
        ],
      },
      {
        h: "The advertiser disclosure matters",
        p: [
          "The FreightWaves article contains an “Advertiser Disclosure” and prominently displays “Get Quote” buttons and telephone numbers alongside recommended companies. Advertising doesn't automatically invalidate editorial content. Affiliate marketing doesn't automatically make a ranking dishonest. Lead generation isn't automatically improper.",
          "But when editorial recommendations and commercial activity exist on the same page, consumers deserve to understand the relationship — and perhaps the simplest question of all: could a small Naples auto transport company that pays the publication absolutely nothing receive the #1 position if the methodology determined it was objectively the best company? Yes or no?",
        ],
        list: [
          "Does the publisher receive money when consumers click quote buttons or call?",
          "Does it receive compensation for leads or completed bookings?",
          "Can recommended companies purchase advertising or enhanced exposure?",
          "Does any commercial relationship influence who is evaluated, displayed or recommended?",
        ],
      },
      {
        h: "And now another question: where are the employees?",
        p: [
          "As we continue investigating this industry, this is something we are going to start documenting. Again, outsourcing is not proof of wrongdoing. But consumers deserve transparency. If you call what appears to be a Naples auto transport company, you deserve to know whether you're speaking with someone in Naples, another state or another country. And if a publication tells you that company is one of the “best companies in Naples,” we believe the publication should know the answer too.",
        ],
        list: [
          "Corporate headquarters and FMCSA registration",
          "Actual office location, owners and executives",
          "Sales-team, dispatch-team and customer-service locations",
          "Whether overseas call centers or overseas contractors are used",
          "Where consumer payment information is processed and personal information is accessible",
        ],
      },
      {
        h: "Show Naples the data",
        p: [
          "This isn't complicated. Any publication issuing “best of” rankings for a specific hometown should be able to tell consumers how it reached that conclusion.",
        ],
        list: [
          "How many actual Naples companies were evaluated — and which were rejected?",
          "How many Naples residents provided the “feedback” the rankings cite, and how were they identified?",
          "How are Google, Yelp, BBB and Trustpilot reviews weighted?",
          "How are review-platform warnings about potentially fake reviews treated?",
          "Where are the recommended companies headquartered, and where are their sales and customer-service operations actually located?",
          "Does the publisher receive compensation connected with these recommendations?",
          "Why do the same national companies repeatedly appear at the top of supposedly localized rankings?",
        ],
      },
      {
        h: "This is bigger than one article",
        p: [
          "The deeper we dig into the auto transport industry, the more we believe consumers have no idea who they are actually dealing with. A website can look local. A telephone number can look local. A Google listing can look local. An address can look American. A company can be federally licensed. None of those things, individually, tell a consumer where the people actually conducting the transaction are located.",
          "This isn't about someone's ethnicity, accent or nationality. It's about business transparency — knowing who has access to American consumers' personal and financial information, who is actually negotiating American transportation transactions, and whether a company marketed as “local” has a meaningful local presence. And it's about whether a publication has done enough research before telling consumers these are the best companies in their hometown.",
          "FreightWaves says these are among the best car shipping companies in Naples. We would like to see exactly how it reached that conclusion.",
        ],
      },
      {
        h: "How to protect yourself as a consumer",
        p: [
          "You don't need an investigation to protect yourself — you need five minutes and the right questions. Before paying any deposit, verify the company directly using sources that can't be bought with an affiliate payout.",
        ],
        list: [
          "Look up the USDOT and MC number on the official FMCSA site and confirm the legal name matches.",
          "Ask where the sales, dispatch and customer-service teams are physically located.",
          "Cross-check scores on BBB, Google and Trustpilot — including review-platform warnings about removed fake reviews.",
          "Ask whether the quote assumes the vehicle runs and drives, and get the broker fee in writing.",
          "Call the phone number on the company's own website — not the one in an ad or a ranking page.",
        ],
      },
    ],
    takeaways: [
      "“Best of” rankings often recommend the same national companies on every city page, regardless of actual local presence.",
      "A U.S. registration or address does not tell you where the sales and customer-service work is performed.",
      "Cross-check any ranking score against BBB, Google and Trustpilot — the gaps can be dramatic.",
      "An “Advertiser Disclosure” next to “Get Quote” buttons means you should ask who gets paid when you click or call.",
      "Verify any broker's USDOT and MC number directly with the FMCSA before paying a deposit.",
    ],
  },
  {
    slug: "fake-google-maps-auto-transport-problem",
    title:
      "The Fake Google Maps Auto Transport Problem: Who Are You Really Calling When You Search \u201CCar Shipping Near Me\u201D?",
    category: "Fraud Prevention",
    date: "2026-08-25",
    readMinutes: 13,
    excerpt:
      "A Google Maps listing is not proof you're dealing with a legitimate local auto transport company. I started investigating local-looking listings across Southwest Florida — the addresses, the images, the names — and comparing them with federal transportation records. Here's what consumers need to know.",
    sections: [
      {
        h: "I started investigating auto transport companies on Google Maps in Southwest Florida. What I found should concern consumers",
        p: [
          "There is something happening on Google Maps that I believe deserves the attention of consumers, Google, the Federal Motor Carrier Safety Administration (FMCSA), the Federal Trade Commission, state regulators and local news organizations.",
          "Search Google Maps for terms such as:",
        ],
        list: [
          "\u201CAuto transport near me.\u201D",
          "\u201CNaples auto transport.\u201D",
          "\u201CBonita Springs car shipping.\u201D",
          "\u201CFort Myers auto transport.\u201D",
        ],
      },
      {
        h: "Everything can look legitimate",
        p: [
          "A consumer may see what appears to be a local transportation company. There may be a local-sounding business name. There may be a Naples, Bonita Springs or Fort Myers address. There may be photographs. There may be reviews. There may be a local telephone number.",
          "To an ordinary consumer, everything can look legitimate.",
          "But I started investigating some of these listings. And I believe consumers need to understand something very important: a Google Maps listing is not proof that you're dealing with a legitimate local auto transport company.",
        ],
      },
      {
        h: "What I started finding",
        p: [
          "As I began looking more closely at auto transport listings throughout Southwest Florida, I started seeing what appeared to me to be a pattern.",
          "I found listings presenting themselves as local auto transport businesses where I could not reconcile the information being presented on Google with the information I expected to find in federal transportation records.",
          "I found addresses that raised questions about whether an auto transport company actually operated there.",
          "I found business names that did not appear to match the federally registered transportation entity I expected to find.",
          "I found images that appeared artificial or inconsistent with the physical businesses being represented.",
          "And I began finding similar-looking listings in multiple cities.",
          "I want to be precise: before calling any particular address \u201Cfake,\u201D any photograph \u201CAI-generated,\u201D or any particular company unlicensed, each fact needs to be individually documented. That is what this investigation is attempting to do.",
          "But the broader pattern is serious enough that consumers should know how to protect themselves now.",
        ],
      },
      {
        h: "An auto transport company isn't like a pizza restaurant",
        p: [
          "This is what makes the situation especially important.",
          "Interstate auto transportation is federally regulated.",
          "Companies arranging interstate transportation as brokers generally operate under FMCSA broker authority. Motor carriers transporting vehicles interstate operate under applicable federal motor-carrier authority.",
          "That means consumers have something much more powerful than Google Maps available to them: federal transportation records.",
          "If someone tells you they are an auto transport broker, ask: What is your MC number? What is your USDOT number? Then verify it.",
          "Do not simply assume that because Google displays a company at an address in Naples, that exact business name and location correspond to the federally authorized transportation entity handling your transaction.",
        ],
      },
      {
        h: "A Google Business Profile does NOT equal an FMCSA license",
        p: [
          "This distinction is incredibly important.",
          "Google determines whether a business can appear on Google Maps. Google does not issue federal motor-carrier or property-broker operating authority. FMCSA does.",
          "A company appearing prominently on Google Maps does not, by itself, establish that:",
        ],
        list: [
          "the business name matches the federally licensed entity;",
          "the person answering the telephone works for that entity;",
          "the displayed address is the transportation company's actual operating location;",
          "the business has active broker or carrier authority;",
          "the company shown on Google is the company that will appear on your contract;",
          "the company handling your payment is the company whose authority you verified.",
        ],
      },
      {
        h: "FMCSA verification checklist: what to ask and how to check it",
        p: [
          "Before you pay a deposit or give a stranger your vehicle, run through this checklist. It takes about five minutes and can save you from a bait-and-switch, an unlicensed broker, or a company that exists only on Google Maps.",
        ],
        list: [
          "Ask for the broker's MC number. Every legitimate interstate auto transport broker has one. If they refuse, won't give it in writing, or say they 'work under another company's authority,' stop.",
          "Ask for the broker's USDOT number. Carriers have USDOT numbers; brokers have MC numbers. Request both if the company claims to be a carrier or a broker-carrier.",
          "Go to safer.fmcsa.dot.gov and click 'Company Snapshot.' Enter the MC or USDOT number exactly as given.",
          "Confirm the legal business name on the FMCSA record matches the name on the company's website, contract and credit-card charge. If it doesn't, ask why — and get the DBA or trade-name registration in writing.",
          "Check the operating status. It should say 'Active' for both the broker (MC) and any motor carrier (USDOT) involved. 'Inactive,' 'Revoked,' or 'Not Authorized' means do not book.",
          "Verify the authority type. A broker needs 'Broker Authority (B)' listed as active. A carrier needs 'Common Carrier Authority (C)' or 'Contract Carrier Authority (Z)' depending on how it operates.",
          "Look at the insurance on file. FMCSA shows whether cargo and liability insurance is current. Ask the company for a certificate of insurance and compare the policy dates and limits with the FMCSA record.",
          "Compare the physical address. The FMCSA record lists a business address. Cross-check it against the Google Maps address, the website contact page, and any invoice or contract you receive.",
          "Call the FMCSA-listed phone number if you are suspicious. Some scammers use numbers that don't connect to the actual licensed entity. A quick call can reveal whether the same people answer.",
          "Verify the carrier at pickup, not just the broker. When the truck arrives, ask the driver for the motor carrier's USDOT number, insurance certificate, and driver's CDL. Photograph the truck's DOT number on the door.",
          "Save screenshots of everything — the Google listing, the FMCSA record, the contract, the receipt, and the carrier's truck and license plate. If something goes wrong, this is your evidence.",
        ],
      },
      {
        h: "The address problem",
        p: [
          "Consumers need to verify those things separately.",
          "One of the first things I started examining was the addresses. If Google tells consumers that an auto transport company is located in Naples, I want to know:",
        ],
        list: [
          "What is actually at that address?",
          "Is there an auto transport office?",
          "Is there signage?",
          "Is the business listed in the building directory?",
          "Does the company identify the same location on its website?",
          "Does the address correspond with public business records?",
          "Most importantly for interstate transportation: does the identity presented to the consumer correspond with the entity appearing in FMCSA records?",
        ],
      },
      {
        h: "When the Google identity doesn't line up",
        p: [
          "A legitimate transportation company can use a home office, shared office, registered-agent address or other lawful business arrangement. An address discrepancy by itself doesn't prove fraud.",
          "But when the Google identity, website identity, telephone identity, address and federal transportation identity don't line up, consumers should stop and investigate before handing over money or vehicle information.",
        ],
      },
      {
        h: "Then there are the images",
        p: [
          "Another issue I believe deserves investigation is the imagery used by some local-looking transportation listings.",
          "Artificial intelligence has made it incredibly easy to create:",
        ],
        list: [
          "Fake-looking office scenes.",
          "People wearing branded uniforms that may never have existed.",
          "Car carriers with impossible or distorted equipment.",
          "Storefronts with generated signage.",
          "Professional-looking \u201Cteam\u201D photographs.",
        ],
      },
      {
        h: "Marketing artwork vs. manufactured evidence",
        p: [
          "A generated image isn't automatically deceptive. Plenty of legitimate businesses use stock photography or AI-generated marketing graphics.",
          "The problem arises when imagery creates the impression that consumers are looking at a company's actual employees, actual office, actual trucks or actual physical location when they are not. That distinction should matter to Google.",
          "If an image is marketing artwork, that's one thing. If an artificial image is being used as apparent evidence of a physical local operation that doesn't exist, that's something entirely different.",
        ],
      },
      {
        h: "I believe Google needs to examine this pattern",
        p: [
          "Google Maps has enormous power over local commerce. Consumers trust it.",
          "If Google tells someone \u2014 AUTO TRANSPORT \u2014 NAPLES, FLORIDA \u2014 and puts a pin on a map, most consumers understandably assume that company has a legitimate business presence at or around that location.",
          "That's why verification standards matter. For a federally regulated industry such as interstate auto transportation, I believe Google should consider additional verification.",
        ],
        list: [
          "Why shouldn't an auto transport broker listing itself on Google provide its MC number?",
          "Why shouldn't a motor carrier provide its USDOT information?",
          "Why shouldn't Google compare the business identity with publicly available FMCSA records?",
          "Why shouldn't businesses claiming storefront locations have to establish that they genuinely operate from those locations?",
        ],
      },
      {
        h: "The name on Google should match the company handling your transportation",
        p: [
          "Google already knows transportation businesses are regulated. There should be a better way to connect Google Business Profile identity with federal transportation identity.",
          "Consumers need to understand another important issue. Suppose you search \u201CNaples Auto Transport.\u201D You call the number. Someone answers as if they represent that company. But then your contract arrives under an entirely different corporate name.",
          "That doesn't automatically mean you're being scammed. Companies can legally use DBAs and trade names. But you should ask questions.",
        ],
        list: [
          "What is the legal company name?",
          "What is the MC number?",
          "What is the USDOT number, if applicable?",
          "Is the Google business name a registered DBA or trade name?",
          "Whose name will appear on my credit-card statement?",
          "Whose name appears on my transportation contract?",
          "Which federally authorized broker is actually arranging my vehicle shipment?",
        ],
      },
      {
        h: "This becomes even more serious when money changes hands",
        p: [
          "Those answers should make sense together. If they don't, stop.",
          "Consumers aren't simply ordering a pizza through these listings. They're potentially providing:",
        ],
        list: [
          "Full names",
          "Home addresses",
          "Telephone numbers",
          "Email addresses",
          "Vehicle information",
          "Pickup and delivery locations",
          "Credit-card information",
          "And sometimes information about exactly when their homes and vehicles will be accessible",
        ],
      },
      {
        h: "And then you are handing someone your car",
        p: [
          "That's sensitive information. Consumers deserve to know exactly who they're giving it to.",
          "Think about what happens next. A stranger arrives. You're potentially handing that person the keys to an asset worth:",
        ],
        list: ["$20,000.", "$50,000.", "$100,000.", "Or considerably more."],
      },
      {
        h: "Before a vehicle leaves your possession",
        p: [
          "That is why identity and licensing matter so much in this industry.",
          "Before a vehicle leaves your possession, you should know:",
        ],
        list: [
          "Who brokered the shipment?",
          "Who is the actual motor carrier?",
          "What is the carrier's USDOT number?",
          "Is its authority active?",
          "Is its insurance current?",
          "Does the truck arriving at your property correspond with the carrier you were told to expect?",
        ],
      },
      {
        h: "I started seeing similar listings beyond one city",
        p: [
          "Consumers should not have to become transportation investigators just to ship a car. But right now, verification is one of the best forms of protection.",
          "What concerned me most was that this did not appear to be limited to one Google Maps listing. As I continued searching, I encountered local-looking auto transport listings associated with multiple communities.",
          "That raises a larger question: are networks of Google Business Profiles being created to capture \u201Cnear me\u201D searches across multiple American cities? If so:",
        ],
        list: [
          "Who owns them?",
          "Where do the telephone numbers terminate?",
          "Do multiple numbers lead to the same call center?",
          "Do the listings share websites, analytics identifiers, tracking numbers, images or infrastructure?",
          "Which FMCSA authority ultimately appears on the contracts?",
        ],
      },
      {
        h: "Consumers should check FMCSA before paying anyone",
        p: [
          "Those are the questions that can turn a suspicious pattern into documented evidence.",
          "If you're shipping a vehicle, take five minutes before giving anyone your credit-card information. Ask the company for its legal business name and federal authority information. Then verify it through FMCSA.",
          "Make sure the company you think you're hiring corresponds with the company you're actually contracting with.",
        ],
        list: [
          "If the salesperson refuses to provide an MC number, that's a major reason to stop.",
          "If you're given an MC number belonging to an entirely different company, ask why.",
          "If the Google name doesn't appear in the federal records, determine whether it's a legitimate DBA.",
          "If the address is completely different, ask why.",
          "If the contract arrives from a different company, read it before signing.",
          "And when the carrier arrives, verify the carrier too.",
        ],
      },
      {
        h: "This is not an attack on legitimate brokers",
        p: [
          "I am a broker. Brokers play an important role in auto transportation.",
          "A legitimate broker finds carriers, negotiates transportation, checks carrier authority and insurance, coordinates logistics and helps consumers navigate an industry they don't deal with every day.",
          "The problem isn't brokerage. The problem is identity without accountability. Consumers should know:",
        ],
        list: [
          "Who sold me this service?",
          "Who is legally responsible for arranging it?",
          "Who has my money?",
          "Who has my personal information?",
          "Who has my vehicle?",
        ],
      },
      {
        h: "I want Google and FMCSA to look at this",
        p: [
          "Those should not be difficult questions to answer.",
          "I believe Google should conduct a systematic review of auto transport Google Business Profiles that claim local locations. Compare:",
        ],
        list: [
          "Google business name",
          "Displayed address",
          "Telephone number",
          "Website",
          "Business registration",
          "FMCSA legal name",
          "FMCSA DBA",
          "MC number",
          "USDOT number",
          "Operating status",
          "Physical/business address",
        ],
      },
      {
        h: "Then examine whether the listings share infrastructure",
        p: [
          "Then examine whether clusters of supposedly unrelated local listings share:",
        ],
        list: [
          "Telephone infrastructure",
          "Websites",
          "Domains",
          "Tracking numbers",
          "Images",
          "Owners",
          "Advertising accounts",
          "Or other identifying information",
        ],
      },
      {
        h: "I am building the evidence",
        p: [
          "If the listings are legitimate, that process will help establish it. If they're not, consumers deserve to know.",
          "I am continuing to document the listings I find. For each one, I want to preserve:",
        ],
        list: [
          "Screenshots of the Google listing",
          "Google business name",
          "Google address",
          "Telephone number",
          "Website/domain",
          "Images displayed on the profile",
          "Date the evidence was captured",
          "FMCSA search results",
          "Corporate registration records",
          "Domain registration/history where available",
          "Connections to other listings",
          "Calls made to the published telephone numbers",
          "The identity given by whoever answers",
        ],
      },
      {
        h: "If you own one of these listings, I want to hear from you",
        p: [
          "The goal isn't to accuse innocent companies. The goal is to create a record that regulators, journalists and Google can independently verify.",
          "If I identify a listing incorrectly, provide the documentation.",
        ],
        list: [
          "If your Google business name is a lawful DBA, show it.",
          "If your address is legitimate, document it.",
          "If your company has active FMCSA authority, provide the MC/USDOT information.",
          "If photographs I question are genuine photographs of your employees, trucks or office, provide evidence.",
        ],
      },
      {
        h: "Google Maps shouldn't be a costume",
        p: [
          "I will correct documented errors. But consumers deserve the same transparency.",
          "A local business listing carries an implicit message: we're here. For consumers, that matters.",
          "A Naples resident searching for a Naples business shouldn't have to wonder whether the \u201Clocal company\u201D exists at the address shown, whether its photographs depict a real operation, whether the business identity corresponds with FMCSA records, or whether the telephone number actually routes somewhere else.",
          "Technology has made it incredibly easy to manufacture the appearance of local presence. AI can manufacture photographs. Virtual telephone systems can manufacture local numbers. Websites can manufacture local pages. Addresses can create the appearance of local offices. Google Maps can give all of it a pin.",
          "But none of those things answer the most important question: WHO ARE YOU ACTUALLY DOING BUSINESS WITH?",
          "Before you ship your car, verify the company. Before you give someone your credit card, verify the company. Before you hand a driver your keys, verify the carrier.",
          "And if Google is going to tell American consumers that a federally regulated transportation business is located in their hometown, I believe Google should be doing a lot more verification too.",
        ],
      },
      {
        h: "Have you encountered one of these listings?",
        p: [
          "If you searched Google for a local auto transport company and later discovered that the company wasn't actually located where Google said it was, I want to hear about it.",
        ],
        list: [
          "Save your screenshots.",
          "Save your contract.",
          "Save the telephone number.",
          "Save your credit-card receipt.",
          "Save the emails and text messages.",
          "Save the MC number you were given.",
        ],
      },
      {
        h: "This investigation is ongoing",
        p: [
          "Documentation is how this gets investigated.",
          "This investigation is ongoing. Any listing or company discussed in future updates will be evaluated individually against publicly available records. An inability to locate a business under a particular Google Maps name does not, by itself, establish that the business lacks required authority; the company may operate under another legal name or registered trade name. Where evidence establishes a discrepancy, I will publish the underlying records so readers can evaluate them for themselves.",
        ],
      },
    ],
    takeaways: [
      "A Google Maps listing is not proof you're dealing with a legitimate local auto transport company.",
      "Google issues map pins; the FMCSA issues operating authority. Verify MC and USDOT numbers in federal records before paying anyone.",
      "If the Google name, website name, contract name and FMCSA legal name don't line up, stop and ask questions.",
      "AI-generated photos, virtual phone numbers and rented addresses can manufacture the appearance of a local office.",
      "Enterprise Auto Transport is a federally licensed, bonded broker — USDOT #2273104, MC #774106 — with offices in Naples, Florida and Minnesota. Verify us the same way.",
    ],
  },
  {
    slug: "carrier-insurance-deductibles-explained",
    title: "Carrier Insurance and Deductibles: What Is Really Covered",
    excerpt:
      "Cargo insurance and the bill of lading belong to the carrier, not the broker. Here is how deductibles decide who pays, why Enterprise is not responsible for damage, and what you should keep in your own insurance policy.",
    category: "Insurance & Claims",
    date: "2026-08-25",
    readMinutes: 9,
    sections: [
      {
        h: "The insurance on your car belongs to the carrier",
        p: [
          "Enterprise Auto Transport is a licensed broker. We arrange your shipment, vet the truck, and help you reach the right people if something goes wrong. The cargo insurance that actually pays for transit damage is held by the motor carrier who physically hauls your vehicle — never by the broker.",
          "That distinction matters more than most people realize. Drivers and carriers are independent businesses, not our employees. We do not own their trucks, hire their drivers, or control their policies. What we can do — and do on every single dispatch — is verify that the carrier has active operating authority with the FMCSA and a current, in-force certificate of cargo insurance before your car is assigned.",
          "You can request a copy of the assigned carrier's certificate of insurance from us before pickup. A refusal to provide one is a red flag, no matter who you are dealing with.",
        ],
        list: [
          "Open carriers we dispatch carry cargo limits starting at $250,000",
          "Enclosed carriers we dispatch carry cargo limits up to $2,000,000",
          "We confirm authority, safety record and insurance certificates before assignment",
          "We cannot control the carrier's internal deductible, claims handling speed or repair-shop choice",
        ],
      },
      {
        h: "We are like a travel agent for your vehicle",
        p: [
          "Think of Enterprise Auto Transport the way you would think of a travel agent. A travel agent finds the best flight, confirms the reservation and handles the paperwork — but the airline owns the plane, sets the fare and is responsible for the flight. We do the same thing for your vehicle move.",
          "Our only responsibility is to negotiate the best realistic rate with licensed carriers on your behalf and present you with the best offer once those negotiations are complete. We cannot change any contract after it is signed, we cannot force a carrier to discount a fare it never agreed to, and legally only the carrier can offer discounts or modify the terms of the physical move.",
        ],
      },
      {
        h: "The bill of lading must come from the carrier",
        p: [
          "The bill of lading is the legal contract and inspection record for the physical move. Because the carrier is the one hauling your vehicle, the controlling BOL is the carrier's document — signed by you and the driver at pickup, and again at delivery.",
          "We provide a printable BOL on our website as a backup in case the driver arrives without one, but the carrier's own BOL is the document that governs the shipment. Make sure you leave pickup with a signed copy in hand or in your inbox before the truck leaves.",
        ],
      },
      {
        h: "Almost every carrier policy has a deductible",
        p: [
          "A cargo policy limit is the maximum the insurer will pay. The deductible is the amount the carrier absorbs before the insurer pays anything at all. Deductibles in this industry commonly run anywhere from $500 to $5,000, and sometimes higher on enclosed equipment.",
          "This is the part almost no auto transport website will tell you plainly: if the damage to your vehicle is less than the carrier's deductible, the insurance company pays nothing. In that situation the carrier is expected to pay the repair out of their own pocket — and reputable carriers do exactly that, because their authority, their Central Dispatch rating and their future loads all depend on settling small claims cleanly.",
          "So a $900 bumper scuff on a truck with a $2,500 deductible is not an insurance claim at all. It is a direct conversation with the carrier. A $12,000 repair on that same truck is an insurance claim, and the carrier still eats the first $2,500 of it.",
        ],
        list: [
          "Damage below the deductible: the carrier pays directly, out of pocket",
          "Damage above the deductible: the insurer pays the balance above it",
          "Damage above the policy limit: rare, but the excess falls back on the carrier",
          "Ask us for the certificate of insurance before pickup — you are entitled to see it",
        ],
      },
      {
        h: "If the carrier fails to pay out of pocket, it is a civil matter",
        p: [
          "Enterprise Auto Transport is not responsible for any damage to any vehicle. The carrier is solely responsible for damage that occurs while your vehicle is in the carrier's custody, control or possession. If the carrier refuses to pay for damage that falls below its deductible, or if the carrier otherwise fails to make you whole, that dispute is a civil matter between you and the carrier.",
          "We will help you identify the carrier, pull the dispatch record and locate the insurance certificate from our file, but we cannot pay claims, adjudicate disputes or force a carrier to settle. That is the legal boundary between a broker and a carrier under federal transportation law.",
        ],
      },
      {
        h: "Keep your own auto insurance active",
        p: [
          "Even though the carrier carries cargo insurance, we recommend keeping your own comprehensive and collision coverage active while the vehicle is in transit. Your personal policy may provide a backstop if the carrier's coverage is delayed, denied or insufficient for a specific loss.",
          "Check with your insurance company before the move to confirm whether your policy covers transport-related damage and what documentation they would need. Do not assume the carrier's policy replaces your own.",
        ],
      },
      {
        h: "What we can verify, and what we honestly cannot",
        p: [
          "We can pull and read a carrier's FMCSA authority status, their safety and inspection history, their Central Dispatch feedback, and their certificate of insurance showing the policy is active on the day of your pickup. We refuse loads to carriers who cannot produce those.",
          "What we cannot do is guarantee how an independent carrier's insurer will adjust a claim, how quickly they respond, or what their deductible will be on your specific load. We are not the insurer and we are not the carrier's employer. Any broker who promises you a guaranteed payout is selling you something they do not control.",
        ],
      },
      {
        h: "The benefits of the broker model",
        p: [
          "Because carriers are independent, we are not locked into one fleet and one route map. We can shop your lane across a national network, hold carriers to a verification standard, and walk away from any truck whose paperwork does not check out — something a single-fleet company cannot do for you.",
          "When a claim happens we already have the dispatch record, the signed bill of lading, the carrier's insurance certificate and a working relationship with that carrier. That is leverage most customers do not have on their own.",
        ],
        list: [
          "Access to thousands of vetted carriers instead of one fleet",
          "Insurance and authority checked before every assignment",
          "We keep the paper trail that makes a claim provable",
          "We push the carrier directly — repeat business is our leverage",
        ],
      },
      {
        h: "The risks, stated plainly",
        p: [
          "The honest risks are these: the carrier's deductible may exceed your damage, which turns the claim into a direct negotiation with the carrier rather than an insurance payout. Claims can take weeks. Some policies exclude acts of nature such as hail on open carriers. Personal items left in the vehicle are never covered. And pre-existing damage that was not documented at pickup will be denied every time.",
          "The single biggest predictor of a successful claim is documentation at pickup — not the size of the policy limit.",
        ],
        list: [
          "Not covered: personal items left in the vehicle",
          "Not covered: pre-existing damage not marked on the pickup bill of lading",
          "Not covered: mechanical failure unrelated to transport",
          "Often excluded on open carriers: hail, flood and other acts of nature",
          "Never covered: tolls, tickets and citations incurred in transit",
        ],
      },
      {
        h: "Protect yourself in ten minutes at pickup",
        p: [
          "Before the driver loads, photograph the truck and trailer, the trailer license plate, and the driver's CDL. Then photograph your car from the front, back, both sides and the top, and take a short video of it actually being loaded. Photos taken the week before do not carry the same weight with an adjuster — they must be from the day of pickup.",
          "At delivery, inspect the car before you sign. Note any new damage on the bill of lading in writing, photograph it immediately, and call us the same day. A signed clean bill of lading is the hardest thing to argue past.",
        ],
        list: [
          "Photos of the truck, trailer, plate and driver's CDL at pickup",
          "Photos of all four sides and the roof of your car before loading",
          "A short video of the car being loaded onto the trailer",
          "Written notes on the delivery bill of lading before you sign",
          "Same-day phone call to us so the claim clock starts clean",
        ],
      },
      {
        h: "How a claim actually gets filed",
        p: [
          "All claims are filed against the carrier and their insurer, not against Enterprise Auto Transport. As the broker we supply the dispatch record, the carrier's insurance information and the bill of lading, and we stay on the carrier until the matter is resolved. We do not carry cargo coverage on your vehicle, and no honest broker does.",
          "If the repair estimate lands under the carrier's deductible, expect the carrier to settle it directly. If it lands above, the insurer takes over and the carrier still absorbs the deductible portion. Either way the carrier — not Enterprise — is the party responsible for making you whole for verified transit damage.",
        ],
      },
    ],
    takeaways: [
      "Cargo insurance is held by the carrier, not the broker — we verify it, we do not underwrite it.",
      "The bill of lading is the carrier's document; you can request a copy of the carrier's insurance certificate from us.",
      "Enterprise is like a travel agent: we negotiate the rate and arrange the move, but the carrier is responsible for the physical transport.",
      "Most carrier policies carry a deductible, commonly $500 to $5,000.",
      "If the damage costs less than the deductible, the insurer pays nothing and the carrier pays out of pocket.",
      "If the carrier refuses to pay, that dispute is a civil matter between you and the carrier; Enterprise is not responsible for vehicle damage.",
      "Keep your own comprehensive and collision coverage active during transport as a backstop.",
      "Pickup-day photos and a properly annotated bill of lading decide most claims.",
    ],
  },
  {
    slug: "should-you-tip-a-car-hauler",
    title: "Should You Tip a Car Hauler? A Practical Guide",
    excerpt:
      "There is no industry standard for tipping car haulers. If you choose to tip, here is a simple guide based on your carrier fee — and when a tip is genuinely appreciated.",
    category: "Preparation",
    date: "2026-08-25",
    readMinutes: 5,
    sections: [
      {
        h: "Is tipping expected in auto transport?",
        p: [
          "There is no industry standard for tipping a car hauler. Most drivers do not expect a tip, and many customers never offer one. The carrier fee you pay already covers the transportation service. That said, we get asked this question often enough that it deserves a straight answer.",
          "Tipping is completely optional. If a driver goes above and beyond — tight scheduling, careful handling of a low-clearance vehicle, extra help at a difficult pickup, or simply outstanding communication — a tip is a nice way to say thank you.",
        ],
      },
      {
        h: "Our suggested tipping guide by carrier fee",
        p: [
          "If you decide to tip, use the carrier fee — the balance paid to the driver at delivery — as your guide. This is the portion that goes directly to the truck, not the broker deposit.",
        ],
        list: [
          "Carrier fee $100 – $300: $15 tip",
          "Carrier fee $301 – $600: $30 tip",
          "Carrier fee $601 – $1,300: $50 tip",
          "Carrier fee $1,301 or more: $75 tip",
        ],
      },
      {
        h: "When a tip matters most",
        p: [
          "Owner-operators own or lease their trucks and are paid by the load. They already build their revenue into the carrier fee, so a tip is a bonus, not a necessity.",
          "Company drivers, on the other hand, are often paid an hourly wage or a fixed salary to operate someone else's truck. For those specific drivers, a cash tip at delivery is usually more meaningful because they do not personally keep the full carrier fee.",
          "Either way, the best drivers will treat your vehicle with care whether you tip or not. Professionalism is not conditional on extra cash.",
        ],
      },
      {
        h: "How to tip at delivery",
        p: [
          "The easiest method is cash at delivery. Most carriers accept cash. Many also accept Zelle. Some will take Venmo or Cash App, though those apps charge the driver a processing fee, so consider adding a little extra if you pay through an app.",
          "Most carriers do not accept credit cards for the balance or a tip. If you want to tip and only have a card, ask the driver what works best for them before the truck leaves.",
        ],
      },
      {
        h: "What matters more than a tip",
        p: [
          "Drivers remember customers who make their job easier. Be on time for pickup and delivery, have the vehicle ready, remove personal items, leave a quarter tank of fuel or less, and turn off toll transponders. A clear driveway and a quick signature mean more than a twenty-dollar bill.",
          "If you want to support a great driver after the fact, a genuine online review or a referral to a friend helps their business long after the tip is spent.",
        ],
      },
    ],
    takeaways: [
      "Tipping is never required in auto transport.",
      "A practical tip range is $15 to $75 based on the carrier fee.",
      "Tips mean the most to hourly company drivers, not owner-operators.",
      "Cash or Zelle is preferred; apps with fees deserve a little extra.",
      "Being ready, on time, and leaving a review often helps the driver more than cash.",
    ],
  },
{
    slug: "auto-transport-identity-chain-of-custody-problem",
    title: "Auto Transport Fraud: The Identity and Chain-of-Custody Gap",
    excerpt:
      "Most auto transport fraud solutions focus on carrier vetting. The real gap is identity: proving the person picking up your car is authorized to take it.",
    category: "Fraud Prevention",
    date: "2026-08-21",
    readMinutes: 8,
    sections: [
      {
        h: "An idea I am building for my clients",
        p: [
          "A recent high-value vehicle theft made the rounds online this week, and the comments followed a familiar pattern. People blamed the load board, the auction, the broker, the carrier, and the platform. Everyone had a theory about where the breakdown happened.",
          "I am not sure that specific case was a chain-of-custody problem. It may have been platform misuse, double-brokering, or a straight theft ring operating inside the marketplace. But the conversation around it points to something that is happening in a lot of cases, and most of the proposed solutions miss a larger point.",
          "This post is about an idea I am actively developing into an application for my own brokerage clients. The goal is to address one slice of the fraud problem: identity and chain of custody.",
          "A carrier can hold a legitimate DOT number, valid operating authority, and proper insurance. A driver can hold a valid CDL. And the wrong person can still show up in a customer's driveway with a truck and a story. The gap is not always the documents. Sometimes the gap is the connection between the documents and the human being standing in front of the vehicle.",
          "The concept is simple. Every person who touches an order — the carrier, the dispatcher, the driver, and even the customer — should be verified through something like an ID.me-style identity check before any vehicle changes hands. A broker could use this same application when brokering loads to make sure the person taking possession is the person authorized to take possession.",
        ],
      },
      {
        h: "Step 1: The carrier verifies its identity once, deeply",
        p: [
          "Real verification should start with the actual motor carrier, tied to FMCSA registration as the authoritative source. This is not simply entering a DOT number into a form. The system needs to answer: who is this company, and who is actually authorized to represent it?",
          "That single verified carrier identity becomes the root of every order that follows. If the root is solid, every downstream check becomes meaningful. If the root is weak, the rest of the chain collapses.",
        ],
      },
      {
        h: "Step 2: Every authorized person gets their own identity",
        p: [
          "Carriers are not one person. An owner-operator may have a dispatcher, an operations manager, drivers, and employees. Each of those individuals should have their own verified account and credential inside the system, confirmed through an ID.me-style identity verification.",
          "The owner should be able to add or remove employees instantly. If a driver leaves the company, their authorization dies with their account. No shared passwords, no generic logins, no employee pretending to be the owner. The system should always know exactly who is who and who is authorized to act for that carrier.",
          "This is the same standard a broker could use when brokering a load. Before a carrier is assigned, every individual involved — the company representative and the assigned driver — has already passed an identity check.",
        ],
      },
      {
        h: "Step 3: Every order requires a new verification",
        p: [
          "In this proposed app, a carrier would not be verified once and then trusted forever. When an order is accepted, the carrier would assign the specific person responsible for the pickup. The order then shows a verified chain:",
        ],
        list: [
          "FMCSA-registered carrier",
          "Verified company",
          "Verified authorized representative",
          "Verified assigned driver",
          "Specific vehicle and order",
        ],
      },
      {
        h: "Step 4: The driver verifies again at pickup",
        p: [
          "When the driver arrives, they authenticate through the app using a secure login, passkey, biometric, or ID.me-style identity verification. The system confirms: this is the verified person, they are still authorized by the carrier, they are assigned to this specific order, and the authorization is active at the time of pickup.",
          "Only after that verification would the system generate a one-time pickup authorization. The credential is temporary and order-specific, so a stolen login or reused code cannot move a different vehicle.",
          "For a broker using this application when brokering loads, this step is the difference between hoping the right carrier shows up and knowing it.",
        ],
      },
      {
        h: "Step 5: The customer verifies before releasing the vehicle",
        p: [
          "The customer should see a verified pickup authorization before handing over the keys. The customer and driver then complete a one-time verification match. If the person does not pass verification, the customer does not release the vehicle. If the carrier cannot verify the assigned person, the carrier does not receive the pickup authorization.",
          "The customer-facing screen might show something like this:",
        ],
        list: [
          "Carrier: ABC Auto Transport LLC",
          "Driver: John Smith",
          "Identity verified through ID.me-style check",
          "Assigned to: This specific vehicle",
          "Authorization: Active",
        ],
      },
      {
        h: "A different question for the industry",
        p: [
          "Carrier vetting still matters. Background checks, insurance verification, and monitoring all have a place. But they are not enough on their own. We also need to ask how to verify every person involved in taking possession of a customer's vehicle, every single time.",
          "A legitimate carrier identity is not enough. A legitimate driver's identity is not enough. The missing piece is the verified connection between the registered carrier, the authorized employee or driver, the specific order, and the actual vehicle being released.",
          "The application I am building for my clients is designed around that standard. Brokers could use it when brokering loads to confirm that every individual in the chain — carrier representative, driver, and customer — has been verified before a vehicle is released.",
          "No ID.me-style verification. No authorization. No vehicle. That is the standard that could move the needle on a large slice of auto transport fraud. It is an idea I am actively working on, not just thinking about.",
        ],
      },
    ],
    takeaways: [
      "Carrier vetting alone does not stop pickup fraud.",
      "The real gap is the verified connection between carrier, employee, driver, order, and vehicle.",
      "An ID.me-style identity check for every individual could close that gap.",
      "Brokers could use this same application when brokering loads to verify who takes possession.",
      "I am currently developing this concept for my own brokerage clients.",
    ],
  },
  {
    slug: "what-car-shipping-actually-costs",
    title: "What Car Shipping Actually Costs in 2026 — And Why Two Quotes Never Match",
    excerpt:
      "A plain-English breakdown of the seven variables carriers price against, and why the cheapest number you are shown is usually the one least likely to survive to pickup.",
    category: "Pricing",
    date: "2026-08-10",
    readMinutes: 8,
    sections: [
      {
        h: "There is no rate card in auto transport",
        p: [
          "Vehicle shipping is not priced like an airline ticket. Every load is a small negotiation between a broker and an independent carrier who is building a route that has to pay for fuel, insurance, tolls, driver hours and an empty return leg. The number you are quoted is a prediction of what a truck will accept for your specific vehicle on your specific lane in the week you want to move.",
          "That is why two companies can quote the same route four hundred dollars apart. One is quoting the market. The other is quoting a number designed to win the phone call.",
        ],
      },
      {
        h: "The seven variables that move your price",
        p: [
          "Almost every quote you receive is built from the same inputs, weighted differently by whoever is doing the math.",
        ],
        list: [
          "Distance and lane density — a run between two metro areas costs less per mile than a rural pickup that pulls a driver off the interstate.",
          "Vehicle size and weight — a full-size truck occupies the space and payload of roughly two sedans, so it prices like two sedans.",
          "Open versus enclosed — enclosed hardware is scarcer and carries far higher insurance limits, typically running 40 to 70 percent above open.",
          "Operating condition — a vehicle that will not roll, steer or brake requires a winch and a driver willing to spend the extra loading time.",
          "Season — snowbird corridors, January auction season and summer relocations all pull trucks toward the same lanes at the same time.",
          "Flexibility — a five-day pickup window buys you a lower rate than a single guaranteed date.",
          "Fuel and current carrier supply — the same lane can swing week to week when diesel moves or trucks reposition.",
        ],
      },
      {
        h: "Why the lowest quote often costs the most",
        p: [
          "The oldest tactic in this industry is to quote below market, collect the booking, then call back three days later saying no driver will take the load and the price has to rise. By then the customer has stopped shopping and is short on time.",
          "A quote is only meaningful if a carrier will actually accept it. Ask any company whether the number they gave you is what will be posted to the carrier network, and whether it can change after you book. The answer tells you more than the price does.",
        ],
      },
      {
        h: "How to compare quotes honestly",
        p: [
          "Line the offers up on the same terms: same transport type, same pickup window, same door-to-door service, same insurance limits. Then ask what the deposit covers and when the balance is due. Our position is simple — the deposit is explained before you book, the balance goes to the driver at delivery, and the rate you accepted is the rate you pay.",
        ],
      },
    ],
    takeaways: [
      "Price is a market prediction, not a published rate.",
      "Distance, size, transport type, condition, season and flexibility drive nearly all of the number.",
      "A quote that no carrier will accept is not a real quote.",
    ],
  },
  {
    slug: "open-vs-enclosed-transport",
    title: "Open or Enclosed: Choosing the Right Trailer for Your Vehicle",
    excerpt:
      "Roughly nine in ten vehicles ship on open carriers — and that is the correct answer for most people. Here is where the extra cost of enclosed genuinely pays for itself.",
    category: "Shipping Types",
    date: "2026-08-04",
    readMinutes: 7,
    sections: [
      {
        h: "What open transport really is",
        p: [
          "Open transport means a full-size multi-car semi carrier hauling seven to ten vehicles on two decks, exposed to the weather the same way a car is exposed while parked outside. It is the method manufacturers use to move new inventory to dealerships, and it is the reason a new car can arrive at a showroom with fourteen miles on the odometer and no damage.",
          "The exposure people worry about is road grime and rain. Actual incidents almost always trace back to poor loading practice, not the absence of walls.",
        ],
      },
      {
        h: "What enclosed transport buys you",
        p: [
          "Enclosed trailers add hard sides and a roof, a lift gate or low-angle ramp for cars with limited clearance, and a much higher cargo insurance limit. On our enclosed fleet that limit is one million dollars, against two hundred fifty thousand on open.",
          "Just as important, enclosed carriers move two to six vehicles instead of ten. Fewer cars means more room around each one, slower and more deliberate loading, and drivers who specialize in high-value freight.",
        ],
      },
      {
        h: "The soft-tie standard",
        p: [
          "On every enclosed load we dispatch, vehicles are secured with padded nylon soft-ties looped over the tires. The straps hold the wheels to the deck, so all tension is carried by the tires and suspension travel is left alone. Nothing hooks to a control arm, subframe or body panel.",
          "The older method — chains through the suspension — is still used by some carriers and is how bent components, scraped undertrays and cracked lips happen. If a vehicle matters to you, ask what secures it before you ask what it costs.",
        ],
      },
      {
        h: "A simple decision rule",
        p: [
          "Choose open for daily drivers, leases, family relocations, dealer trades and anything you would comfortably park on a city street overnight. Choose enclosed for collector and classic cars, exotics and supercars, low-clearance builds, fresh restorations, show vehicles, and anything whose replacement value or paint would be difficult or expensive to restore.",
        ],
      },
    ],
    takeaways: [
      "Open carriers are the industry default and are safe when loaded correctly.",
      "Enclosed adds weather protection, lift-gate loading and a $1M cargo limit.",
      "Soft-tie wheel straps matter more than trailer walls for preventing damage.",
    ],
  },
  {
    slug: "prepare-your-car-for-pickup",
    title: "The Pre-Pickup Routine: Preparing a Vehicle So Nothing Goes Wrong",
    excerpt:
      "Most transport disputes are avoidable and start days before the truck arrives. This is the routine we walk every customer through — including the toll transponder step almost everyone forgets.",
    category: "Preparation",
    date: "2026-07-28",
    readMinutes: 6,
    sections: [
      {
        h: "Wash it — then photograph when the truck arrives, not before",
        p: [
          "Wash the vehicle ahead of time so damage is visible. Dirt hides chips, scuffs and stone rash, and a driver cannot note a defect on the Bill of Lading that neither of you can see. But the photos that count are the ones you take when the truck is there. Pictures taken days earlier cannot be proven to show the car's condition at the moment custody changed hands.",
          "When the carrier pulls up, photograph the front, the back and both sides of the car, plus the roof, the wheels, the windshield, the interior and the odometer. Photograph any existing damage close up with something for scale. Also photograph the driver's truck, the side of the cab showing the DOT number, the license plate, and ask for his driver's license and photograph that too. Confirm it is the carrier we assigned you. This is your responsibility, and this set of images is your entire evidence file if a claim is ever needed.",
        ],
        list: [
          "Photos of the front, back and both sides at pickup — with the truck in frame",
          "Side of the cab with the DOT number, and the license plate",
          "The driver's license, and confirmation it matches the carrier we assigned",
          "A copy of the Bill of Lading in your hand at pickup and again at delivery",
        ],
      },
      {
        h: "Get your copy of the Bill of Lading — both times",
        p: [
          "Getting a copy of the Bill of Lading is your responsibility, not ours. You must get one at pickup and another at delivery. If the driver says he will email it, check your email and confirm it arrived before he leaves the property.",
          "If the carrier rushes you and pushes the clipboard at you to sign, that is a red flag and your signal to slow down. Walk around the car with him and inspect it. If there is damage and the driver tells you to sign and he will take care of it, do not take him at his word — write every bit of damage on the document anyway. What is written and photographed is your evidence; a verbal promise is not.",
        ],
      },
      {
        h: "Turn off your toll transponder",
        p: [
          "Remove or fully deactivate any SunPass, E-ZPass, PikePass or similar device before pickup. A transponder left active on a truck deck will register at gantries the whole way across the country and bill your account for tolls you never drove through.",
          "Tolls, tickets and citations incurred while the vehicle is in transit remain the vehicle owner's responsibility. Sliding the device into its shielded pouch is not enough on every system — take it out of the windshield and put it in your luggage.",
        ],
      },
      {
        h: "Fuel, fluids and mechanical checks",
        p: [
          "Leave roughly a quarter tank of fuel. That is enough for loading and unloading without adding unnecessary weight. Check tire pressure, top off the battery charge, and top up fluids so the driver can start and move the car without troubleshooting.",
          "Any active leak needs to be disclosed. A vehicle dripping oil on the deck can damage the car parked below it, and the carrier may refuse the load at the door.",
        ],
      },
      {
        h: "Strip it down",
        p: [
          "Remove toll tags, parking passes, garage remotes, dash cams, loose electronics and anything in the trunk you would miss. Retract or remove aftermarket antennas, fold mirrors, and secure or remove spoilers, bike racks and roof boxes.",
          "Personal items are not covered by cargo insurance. Carriers are licensed to move vehicles, not household goods, and loaded weight affects DOT compliance at the scales.",
        ],
        list: [
          "Quarter tank of fuel",
          "Toll transponder removed and deactivated",
          "Alarm disabled or instructions written down for the driver",
          "Two sets of keys — one travels, one stays with you",
          "Photos of every panel, plus the odometer",
        ],
      },
    ],
    takeaways: [
      "Wash early, but take your photos when the truck arrives — earlier pictures cannot be proven.",
      "Get a copy of the Bill of Lading at pickup and at delivery, and never sign a rushed clipboard.",
      "Deactivate and remove toll transponders; transit tolls stay with the owner.",
      "Leave a quarter tank, empty the car, and disclose any leaks or quirks.",
    ],
  },
  {
    slug: "bill-of-lading-explained",
    title: "The Bill of Lading Is the Only Document That Matters",
    excerpt:
      "It is the inspection record, the receipt and the contract in one page. How to read it at pickup, what to write at delivery, and the one line that ends a claim before it starts.",
    category: "Preparation",
    date: "2026-07-21",
    readMinutes: 6,
    sections: [
      {
        h: "What the document actually is",
        p: [
          "The Bill of Lading, usually shortened to BOL, is the condition report the driver completes with you at pickup and again at delivery. It records existing damage, mileage, the vehicles's condition and the fact that custody changed hands. Insurance adjusters treat it as the authoritative record of what the car looked like when it went on the truck.",
        ],
      },
      {
        h: "At pickup: slow the process down",
        p: [
          "Walk the vehicle with the driver rather than beside the paperwork. Point out every mark you already photographed and confirm it appears on the diagram. If a scratch you can see is not on the sheet, ask for it to be added before you sign.",
          "Take a photo of the completed form with your phone and keep the copy the driver hands you. If you will not be present at pickup, name someone who will be and give them the same instructions.",
        ],
      },
      {
        h: "At delivery: never sign clean in the dark",
        p: [
          "Inspect before you sign. If delivery lands after sunset, use a flashlight and walk every panel, or ask to move to a lit area. Compare against your pickup photos rather than your memory.",
          "If you find new damage, write it on the BOL in specific language — panel, location, type of mark — before signing, and photograph both the damage and the annotated document. A signature on a clean delivery BOL is the strongest evidence a carrier has that the vehicle arrived undamaged, and it is very difficult to walk back later.",
        ],
      },
      {
        h: "What to do next",
        p: [
          "Notify us the same day. We coordinate the claim with the carrier and their insurer, provide the dispatch record, and stay on it until it resolves. Keep the annotated BOL, both photo sets and any repair estimates together in one place.",
        ],
      },
    ],
    takeaways: [
      "The BOL is the inspection record insurers rely on.",
      "Add missing pre-existing damage at pickup, before signing.",
      "Note new damage on the delivery BOL before you sign — not afterward.",
    ],
  },
  {
    slug: "how-long-car-shipping-takes",
    title: "Realistic Transit Times: How Long Shipping a Car Actually Takes",
    excerpt:
      "Pickup windows, drive-day math, hours-of-service limits and the difference between a promise and a guarantee.",
    category: "Shipping Types",
    date: "2026-07-14",
    readMinutes: 6,
    sections: [
      {
        h: "Two clocks, not one",
        p: [
          "Every shipment has a pickup window and a transit time, and people routinely confuse them. The pickup window is how long it takes to place your vehicle with a carrier already running your lane — typically one to five days. Transit time starts when the car is loaded.",
        ],
      },
      {
        h: "Drive-day math",
        p: [
          "Federal hours-of-service rules cap a driver at eleven driving hours in a fourteen-hour on-duty window, and multi-car carriers average roughly four hundred to five hundred miles a day once you account for loading, unloading, fuel and traffic.",
        ],
        list: [
          "Under 500 miles: 1–2 days",
          "500–1,000 miles: 2–3 days",
          "1,000–1,500 miles: 3–5 days",
          "1,500–2,000 miles: 4–6 days",
          "Coast to coast: 7–10 days",
        ],
      },
      {
        h: "What stretches a timeline",
        p: [
          "Rural pickups and deliveries, mountain weather, holiday traffic, seasonal congestion on snowbird corridors, and multi-stop routes where your car is not the first drop. None of these are unusual and all of them are why reputable companies quote ranges rather than exact hours.",
        ],
      },
      {
        h: "When you need a date, not a range",
        p: [
          "Expedited service buys you a narrow guaranteed pickup window and priority placement, sometimes on a truck carrying fewer vehicles. It costs more because the carrier is reorganizing a route around you. If you are closing on a house, catching a flight or handing over a lease, it is usually worth it.",
        ],
      },
    ],
    takeaways: [
      "Pickup window and transit time are separate clocks.",
      "Plan on 400–500 miles per day of transit.",
      "Guaranteed dates exist, but they are a paid upgrade.",
    ],
  },
  {
    slug: "snowbird-shipping-guide",
    title: "The Snowbird Playbook: Booking Seasonal Transport Before the Lanes Fill",
    excerpt:
      "Southbound in the fall, northbound in the spring, and everyone calling in the same two weeks. How to time a seasonal move so you are not paying peak rates for leftover capacity.",
    category: "Seasonal",
    date: "2026-07-07",
    readMinutes: 7,
    sections: [
      {
        h: "The season is more predictable than the market",
        p: [
          "Seasonal residents move on a calendar the whole industry knows. Vehicles head south from the Northeast and Midwest through the fall and return north in the spring. Because tens of thousands of people run the same play in the same weeks, carrier capacity on those corridors tightens sharply and rates follow.",
        ],
      },
      {
        h: "Book three to six weeks ahead",
        p: [
          "For October through December arrivals and March through May departures, three to six weeks of lead time is the window that consistently gets a good carrier at a fair rate. Inside two weeks you are bidding against everyone who waited, and the trucks that remain are the ones that did not fill at normal rates.",
          "Returning clients get priority dispatch through our preferred carrier network — we know the vehicle, the community access rules and the delivery contact, so the load posts faster and cleaner.",
        ],
      },
      {
        h: "Gated communities and delivery logistics",
        p: [
          "A ten-car semi cannot enter most gated neighborhoods, and many associations restrict commercial vehicles outright. That is a logistics detail, not a problem: we arrange a nearby meeting point — a plaza lot, a wide shoulder outside the gate, a church or shopping center — and the driver handles the last stretch with you.",
          "Tell us the community name when you book. In Collier and Lee County we already know which addresses need a staging point and which will take a truck at the door.",
        ],
      },
      {
        h: "Two cars, one household",
        p: [
          "If you are moving more than one vehicle on the same route, ship them together. Two cars on the same truck almost always price better per unit than two separate bookings, and both arrive on the same day with the same driver.",
        ],
      },
    ],
    takeaways: [
      "Book 3–6 weeks out for fall arrivals and spring departures.",
      "Returning clients receive priority dispatch.",
      "Expect a nearby meeting point instead of gate-side delivery.",
    ],
  },
  {
    slug: "shipping-a-classic-car",
    title: "Moving a Classic: What Changes When the Car Is Irreplaceable",
    excerpt:
      "Valuation, tie-down method, fluid handling and driver experience — the four things that separate collector transport from a standard booking.",
    category: "Specialty Vehicles",
    date: "2026-06-30",
    readMinutes: 7,
    sections: [
      {
        h: "Establish the value before you book",
        p: [
          "Cargo insurance pays against documented value, not sentiment. Before shipping a collector car, have a current appraisal or an agreed-value policy in hand and share the number with us so the load is matched to a carrier whose coverage limit actually covers it.",
          "Our enclosed carriers run one million dollars in cargo coverage. If your vehicle sits above that, tell us early — it is arrangeable, but not on short notice.",
        ],
      },
      {
        h: "Tie-downs are the whole conversation",
        p: [
          "Insist on soft-ties over the tires. Padded nylon straps hold the wheels to the deck and let the suspension work normally through road inputs. Chains routed through control arms or across a subframe concentrate load on components never designed for it, and on an original car those parts may not be replaceable.",
        ],
      },
      {
        h: "Clearance, fluids and quirks",
        p: [
          "Low front lips, side pipes and period-correct ride heights need a lift gate or a very low-angle ramp — not a standard steel ramp. Tell us the clearance figure rather than estimating it.",
          "Older carburetted cars, cars with battery cutoffs, weak starters and manual chokes all need a note for the driver. Write down the starting sequence and leave it on the seat. A driver who knows the procedure will not be cranking your engine in a parking lot.",
        ],
      },
      {
        h: "Document like an insurer would",
        p: [
          "Photograph the car under even light from every angle, including underbody shots if it has been restored. Note existing paint flaws, chrome pitting and interior wear. On the BOL, be exhaustive — every mark you record at pickup is a mark you never have to argue about at delivery.",
        ],
      },
    ],
    takeaways: [
      "Document value before booking; coverage pays against documentation.",
      "Soft-ties only — never chains through original suspension.",
      "Write down starting quirks and clearance for the driver.",
    ],
  },
  {
    slug: "shipping-an-inoperable-vehicle",
    title: "Shipping a Car That Will Not Start",
    excerpt:
      "Non-running does not mean unshippable. What the carrier needs to know, what equipment gets used, and why an honest disclosure saves you money.",
    category: "Specialty Vehicles",
    date: "2026-06-23",
    readMinutes: 5,
    sections: [
      {
        h: "Define the condition precisely",
        p: [
          "In dispatch terms, an inoperable vehicle is one that cannot move under its own power, but the details matter enormously. A car that rolls, steers and brakes can be winched onto a deck in minutes. A car with seized brakes, a locked steering column or missing wheels needs different equipment and sometimes a different truck entirely.",
        ],
      },
      {
        h: "Why disclosure protects you",
        p: [
          "If a driver arrives expecting a running car and finds one that will not roll, the load can be refused at the door and the whole booking restarts. Telling us the exact condition up front means the vehicle is posted to carriers with winch-equipped trucks, priced correctly the first time, and picked up on schedule.",
          "The surcharge for non-running vehicles typically runs one hundred to two hundred dollars over the standard rate. It reflects the equipment and the extra loading time, and it is far cheaper than a failed pickup.",
        ],
      },
      {
        h: "What to prepare",
        p: [
          "Air the tires if they hold pressure, clear the path so a winch cable can reach, disconnect any alarm that fires when the battery is jumped, and have keys available even if the car will not start.",
        ],
        list: [
          "Tell us whether it rolls, steers and brakes",
          "Note missing wheels, seized brakes or a locked column",
          "Ensure a flat, accessible loading area",
          "Keys available for steering and gear selection",
        ],
      },
    ],
    takeaways: [
      "Most non-running cars ship fine on winch-equipped carriers.",
      "Rolls, steers and brakes is the question that sets the price.",
      "Disclose the condition early to avoid a refused pickup.",
    ],
  },
  {
    slug: "door-to-door-vs-terminal",
    title: "Door-to-Door or Terminal: Which Delivery Method Fits Your Move",
    excerpt:
      "Door-to-door is the default for good reason, but there are real cases where a terminal makes life easier. Here is the honest comparison.",
    category: "Shipping Types",
    date: "2026-06-16",
    readMinutes: 5,
    sections: [
      {
        h: "What door-to-door really means",
        p: [
          "It means the driver gets as close to your address as a full-size semi can legally and safely operate. On a wide suburban street that is your driveway. In a dense city, a gated community or a street with low branches and tight turns, it is a nearby lot you agree on with the driver, usually within a few minutes of the address.",
          "This is normal practice, not a downgrade. A ten-car carrier is roughly eighty feet long and cannot legally back down most residential streets.",
        ],
      },
      {
        h: "When a terminal is the better answer",
        p: [
          "Terminal service means dropping at or collecting from a storage yard on the carrier's route. It can help when your schedule is unpredictable, when you are traveling and cannot meet a driver, or when you need the vehicle stored for a few days between legs of a move.",
          "The tradeoffs are real: your car sits in a yard, storage fees accrue after the free window, and you drive to the terminal on both ends. For most customers the savings do not justify it.",
        ],
      },
      {
        h: "How to make either work",
        p: [
          "Give us a real mobile number for the person meeting the truck, name a backup contact, and mention up front if your address has a gate code, HOA restrictions, a narrow entrance or overhead wires. Every one of those is easy to plan around and expensive to discover on delivery day.",
        ],
      },
    ],
    takeaways: [
      "Door-to-door means as close as an 80-foot truck can safely reach.",
      "Terminals suit unpredictable schedules but add storage and driving.",
      "Flag gates, HOA rules and tight streets when you book.",
    ],
  },
  {
    slug: "shipping-a-car-you-bought-online",
    title: "You Bought a Car Two States Away. Now What?",
    excerpt:
      "Auction and private-sale purchases have their own rules — release authorization, gate fees, storage clocks and inspection you will not be present for.",
    category: "Buying & Selling",
    date: "2026-06-09",
    readMinutes: 7,
    sections: [
      {
        h: "Confirm who can release the vehicle",
        p: [
          "A carrier cannot pick up a car that nobody is authorized to hand over. Before you book, confirm the seller, dealership or auction house has the title cleared, the release paperwork issued and a named contact who will be on site during business hours.",
          "Auctions in particular require the buyer number, a gate pass and sometimes payment confirmation before a driver is allowed onto the lot. Send us those details when you book and we pass them straight to dispatch.",
        ],
      },
      {
        h: "Watch the storage clock",
        p: [
          "Auction yards typically allow a few free days after sale, then bill storage daily. Those fees are charged to the buyer, not the carrier, and they can outrun the shipping cost quickly. Book transport the same day you win the vehicle rather than after the title arrives.",
        ],
      },
      {
        h: "Inspection when you cannot be there",
        p: [
          "You will not be present at pickup, so the seller or auction staff signs the Bill of Lading on your behalf. Ask them to photograph the vehicle before loading and send the images to you along with a copy of the signed BOL. Without that pre-load record, distinguishing transport damage from pre-existing condition at delivery is nearly impossible.",
          "At delivery, inspect against those photos, not against the listing pictures. Listing photos are taken to sell a car.",
        ],
      },
      {
        h: "Dealer and wholesale moves",
        p: [
          "If you are moving inventory rather than a single purchase, multi-unit loads price better and dispatch faster than one-off bookings. Consistent lanes let us keep the same carriers on your account, which is worth more over a year than shaving twenty dollars off any single move.",
        ],
      },
    ],
    takeaways: [
      "Confirm release authorization and a live on-site contact before booking.",
      "Auction storage fees start fast — book transport immediately.",
      "Get pre-load photos when you cannot attend the pickup.",
    ],
  },
  {
    slug: "car-shipping-insurance-explained",
    title: "Who Actually Pays If Your Car Is Damaged in Transit",
    excerpt:
      "Cargo coverage, deductibles, what is excluded, and the difference between the broker who arranged the move and the carrier who performed it.",
    category: "Pricing",
    date: "2026-06-02",
    readMinutes: 7,
    sections: [
      {
        h: "The carrier carries the coverage",
        p: [
          "Every carrier we dispatch holds active cargo insurance and operating authority we verify before your vehicle is assigned. On open carriers that limit is two hundred fifty thousand dollars; on enclosed it is one million. That policy — the motor carrier's — is what responds to transport damage.",
          "We are a licensed broker. We arrange transport, vet the carrier, and coordinate any claim on your behalf. The carrier performing the move is responsible for damage to the vehicle and for injury to persons while it is in their custody.",
        ],
      },
      {
        h: "What cargo insurance covers",
        p: [
          "Damage that occurs while the vehicle is in the carrier's care: loading and unloading incidents, tie-down damage, road debris strikes, and collision or fire involving the transport truck.",
        ],
        list: [
          "Covered: transit damage, loading and unloading damage, carrier accidents",
          "Not covered: personal items left in the vehicle",
          "Not covered: pre-existing damage recorded on the pickup BOL",
          "Not covered: mechanical failure not caused by transport",
          "Not covered: acts of nature such as hail or flood in some policies",
          "Not covered: tolls, tickets or citations incurred in transit",
        ],
      },
      {
        h: "What weakens a claim",
        p: [
          "Signing a clean delivery BOL, having no pickup photos, waiting a week to report, moving the vehicle through a car wash before documenting, or repairing the damage before an adjuster sees it. Each of these hands the insurer a reason to question the timeline.",
        ],
      },
      {
        h: "If something happens",
        p: [
          "Note the damage on the BOL before signing, photograph it immediately from multiple distances, and call us the same day. We supply the dispatch record and carrier insurance details, file with the carrier's insurer, and follow the claim through to resolution.",
        ],
      },
    ],
    takeaways: [
      "The motor carrier's cargo insurance policy covers transit damage — $250k open, up to $2M enclosed insurance coverage.",
      "Personal items, tolls and pre-existing damage are excluded.",
      "Document at pickup and annotate at delivery, or the claim gets hard.",
    ],
  },
  {
    slug: "shipping-an-electric-vehicle",
    title: "Shipping an EV: Weight, Charge Level and Transport Mode",
    excerpt:
      "Electric vehicles ship on the same trucks as everything else, with three details that need to be right before the driver arrives.",
    category: "Specialty Vehicles",
    date: "2026-05-26",
    readMinutes: 5,
    sections: [
      {
        h: "Weight changes the math",
        p: [
          "Battery packs make EVs substantially heavier than comparable combustion cars — often six hundred to a thousand pounds more. Carriers manage total load weight against DOT axle limits, so a heavy vehicle can occupy the payload budget of a larger slot and price accordingly. Give us the exact model when you quote so the estimate holds.",
        ],
      },
      {
        h: "Charge level and transport mode",
        p: [
          "Ship with roughly twenty to fifty percent state of charge. That is enough to load, maneuver and unload with margin, without carrying an unnecessarily full pack. Vehicles sitting on a deck for a week will lose some charge to standby draw.",
          "Set the correct transport or tow mode before handing over the keys, and write the procedure down for the driver — the sequence differs by manufacturer and is not obvious to someone seeing the car for the first time. Disable sentry and always-on camera modes so they do not drain the pack in transit.",
        ],
      },
      {
        h: "Low clearance and regenerative braking",
        p: [
          "Many EVs sit low with a flat underbody tray. Mention the clearance figure and any air suspension so the carrier brings the right ramp angle or a lift gate. Regenerative braking behavior at very low speeds also catches unfamiliar drivers off guard on a steel deck — a quick note solves it.",
        ],
      },
    ],
    takeaways: [
      "EV weight can shift the price; give the exact model.",
      "Ship at 20–50% charge with sentry modes disabled.",
      "Write down the transport-mode procedure for the driver.",
    ],
  },
  {
    slug: "military-pcs-vehicle-shipping",
    title: "PCS Orders and Your Vehicle: Shipping Around a Military Move",
    excerpt:
      "Base access, orders timing, flexible windows and what to do when your report date moves before your car does.",
    category: "Seasonal",
    date: "2026-05-19",
    readMinutes: 6,
    sections: [
      {
        h: "Build the schedule around the report date",
        p: [
          "PCS timelines shift, so book with a pickup window rather than a single date and tell us the real constraint — the date you must be at the new duty station, not the date you would prefer the car to leave. That lets dispatch work backward and keeps options open if orders change.",
        ],
      },
      {
        h: "Base access is a logistics detail",
        p: [
          "Most installations restrict commercial carrier access, and a driver without a base pass will not get through the gate. Plan on meeting off-post at an agreed lot, or arrange visitor credentials in advance if your installation allows it. Tell us which at booking so the driver is briefed.",
        ],
      },
      {
        h: "Paperwork and authorization",
        p: [
          "If you deploy before the vehicle moves, provide written authorization naming the person who will release it, with their contact number. The same applies at delivery. A power of attorney is not always required but a signed authorization plus a reachable contact almost always is.",
        ],
      },
      {
        h: "Overseas legs",
        p: [
          "For OCONUS moves, domestic transport gets your vehicle to the port and the ocean leg is handled separately. Book the domestic side with buffer days ahead of the vessel cut-off — port storage and missed sailings are far more expensive than a few extra days of lead time.",
        ],
      },
    ],
    takeaways: [
      "Book a window, not a date; orders move.",
      "Expect to meet off-post unless base access is arranged in advance.",
      "Leave written release authorization if you deploy first.",
    ],
  },
  {
    slug: "avoiding-auto-transport-scams",
    title: "How to Spot an Auto Transport Scam Before You Pay a Deposit",
    excerpt:
      "Fake companies clone real brand names, real DOT numbers and real review pages. Six checks that take five minutes and rule out almost all of them.",
    category: "Pricing",
    date: "2026-05-12",
    readMinutes: 6,
    sections: [
      {
        h: "The pattern is always the same",
        p: [
          "A quote arrives well below every other offer. There is urgency — the truck is in your area today, the rate expires tonight. Payment is requested by wire, gift card, cryptocurrency or a peer-to-peer app. After the deposit clears, the phone number stops answering, or the price triples.",
        ],
      },
      {
        h: "Six checks that take five minutes",
        p: [
          "Every legitimate broker and carrier is registered and verifiable. Run these before any money moves.",
        ],
        list: [
          "Look up the MC and USDOT number in the FMCSA licensing database and confirm the legal name matches the company contacting you.",
          "Confirm the phone number and email domain match the ones published on the company's own website.",
          "Refuse wire transfers, gift cards, crypto and peer-to-peer payment apps — legitimate companies take cards.",
          "Check the state business registry for the company's registered entity.",
          "Get the quote and the terms in writing, including when the deposit is charged.",
          "Be suspicious of a quote far below every other bid; it is usually the setup for a later increase.",
        ],
      },
      {
        h: "Impersonation of real companies",
        p: [
          "Increasingly the scam is not a fake company but a fake version of a real one — same name, similar domain, spoofed caller ID, copied reviews. Verify the contact details independently from the company's own site rather than trusting the ones in the email you received.",
          "Our only phone number is (239) 273-4649 and our only email is support@enterpriseautotransport.com. Anything else using our name is not us.",
        ],
      },
    ],
    takeaways: [
      "Verify MC and USDOT numbers against FMCSA before paying anything.",
      "Never pay by wire, crypto, gift card or peer-to-peer app.",
      "Confirm contact details from the company's own site, not the inbound message.",
    ],
  },
  {
    slug: "enclosed-car-shipping-cost-2026",
    title: "How Much Does Enclosed Car Shipping Cost in 2026?",
    excerpt:
      "Enclosed car shipping typically costs 40% to 70% more than open transport. Here is what drives the price and how to decide whether the upgrade is worth it for your vehicle.",
    category: "Shipping Types",
    date: "2026-08-18",
    readMinutes: 7,
    sections: [
      {
        h: "The 2026 enclosed car shipping price range",
        p: [
          "In 2026, enclosed auto transport on a common lane generally runs between $1.20 and $2.00 per mile, with shorter moves priced higher per mile than cross-country hauls. A 1,000-mile enclosed shipment often falls between $1,200 and $1,800, while a 2,500-mile coast-to-coast move can range from $2,400 to $4,000 depending on the vehicle and lane.",
          "Those numbers are not rate cards. They are market snapshots. Enclosed trailers are scarcer than open carriers, and the drivers who own them expect higher revenue per load because the equipment costs more and the insurance limits are higher.",
        ],
      },
      {
        h: "What makes enclosed transport cost more",
        p: [
          "Four things move the price more than distance alone.",
        ],
        list: [
          "Trailer scarcity — there are far fewer enclosed haulers on the road, so tight lanes command a premium.",
          "Higher cargo limits — enclosed carriers routinely carry $1,000,000 in coverage, and that insurance cost is built into the rate.",
          "Specialized handling — lift gates, soft-tie wheel straps, and slower loading protect the vehicle but take more driver time.",
          "Vehicle value and size — a wide-body exotic or lifted truck may consume more deck space or require extra care.",
        ],
      },
      {
        h: "When the extra cost is justified",
        p: [
          "Enclosed shipping is worth the premium when the cost of repair or repaint exceeds the shipping upcharge. Exotics, classics, freshly restored vehicles, and very low-clearance builds fall into this category. For a daily driver or standard SUV, the economics usually favor open transport.",
          "The real question is not whether enclosed is better — it is whether your vehicle needs the protection enough to pay for it. If a stone chip, water spot, or sun exposure would ruin your week, enclosed is the right choice.",
        ],
      },
      {
        h: "How to get an accurate enclosed quote",
        p: [
          "The only way to price your move correctly is to quote the actual vehicle, lane, and dates. Instant online calculators can guess, but they rarely account for seasonal carrier supply or whether your vehicle needs a lift gate. Use our Get a Quote form to receive a real enclosed car shipping quote from a coordinator who prices the lane against live carrier availability.",
        ],
      },
    ],
    takeaways: [
      "Enclosed shipping in 2026 typically costs 40–70% more than open transport.",
      "Scarcity, higher insurance, specialized handling, and vehicle size drive the premium.",
      "Enclosed is worth it when repair or repaint costs exceed the shipping upcharge.",
    ],
  },
  {
    slug: "is-my-car-fully-insured-enclosed-transport",
    title: "Is My Car Fully Insured During Enclosed Transport?",
    excerpt:
      "Enclosed carriers usually carry $1 million in cargo coverage, but full insurance is a myth. Learn what is actually covered and how deductibles work.",
    category: "Shipping Types",
    date: "2026-08-18",
    readMinutes: 6,
    sections: [
      {
        h: "The coverage that comes with enclosed transport",
        p: [
          "Most enclosed auto carriers on our network carry $1,000,000 in cargo insurance. That is four times the typical $250,000 limit on an open carrier, and it is one of the main reasons collectors and exotic owners choose enclosed transport.",
          "But a policy limit is not the same as a blank check. Every cargo policy has exclusions, deductibles, and conditions. The carrier's insurance covers damage caused by the carrier's negligence during transit. It does not cover pre-existing damage, personal items left inside the car, or mechanical failures unrelated to transport.",
        ],
      },
      {
        h: "Why no vehicle is 100% insured",
        p: [
          "The idea that a car is fully insured is a myth. Every policy has a deductible, and if the deductible is higher than the claim amount, the carrier may be left paying out of pocket. A carrier with a $5,000 deductible is unlikely to file a claim for $500 in damage, which means the repair may not happen through insurance at all.",
          "This is why we do not chase the cheapest carrier. A cheap rate often means a carrier with a high deductible, thin coverage, or a poor safety record. If your car is damaged, the difference between a quality carrier and a budget carrier is whether the claim is paid or becomes a civil dispute.",
        ],
      },
      {
        h: "What to ask before you book",
        p: [
          "Request the certificate of insurance directly. Confirm the cargo limit, the deductible, and whether the policy is active for your shipment dates. A reputable broker should provide this without hesitation.",
          "At Enterprise Auto Transport, we verify carrier insurance before dispatch and send you the driver's direct contact information. We also recommend photographing every panel at pickup and delivery so any new damage is documented immediately.",
        ],
      },
      {
        h: "Get a quote with verified coverage",
        p: [
          "If you are shipping a high-value vehicle, the insurance conversation matters as much as the price. Use our Get a Quote form and ask for an enclosed carrier with a certificate of insurance included. We will confirm the coverage before any truck is dispatched.",
        ],
      },
    ],
    takeaways: [
      "Enclosed carriers typically carry $1,000,000 in cargo coverage.",
      "Every policy has deductibles and exclusions — full insurance is a myth.",
      "Always request the certificate of insurance before booking.",
    ],
  },
  {
    slug: "ship-luxury-car-across-country",
    title: "How to Ship a Luxury Car Across the Country",
    excerpt:
      "Moving a luxury vehicle coast to coast takes more than a low quote. Here is how owners protect value, avoid delays, and choose the right carrier.",
    category: "Specialty Vehicles",
    date: "2026-08-18",
    readMinutes: 7,
    sections: [
      {
        h: "Start with the trailer type",
        p: [
          "Luxury cars should almost always ship enclosed. Open carriers expose the paint to road debris, weather, and the risk of another vehicle leaking fluid above yours. Enclosed trailers add hard walls, a roof, and usually a hydraulic lift gate that eliminates ramp scrape risk for low-clearance vehicles.",
          "If the vehicle is worth more than $75,000, has custom paint, or sits lower than stock, enclosed transport is not an upgrade — it is the baseline.",
        ],
      },
      {
        h: "Vet the broker and the carrier",
        p: [
          "The broker sets the price, but the carrier does the actual work. Ask for the carrier's MC number, insurance certificate, and safety record before dispatch. A broker who cannot provide these details is not vetting the trucks that will touch your car.",
          "At Enterprise Auto Transport, we dispatch only carriers we would trust with our own vehicles. Our 99.9% carrier rating on Central Dispatch exists because drivers want to work with us — and that matters when your car needs to move on a specific lane.",
        ],
      },
      {
        h: "Prepare the vehicle for white-glove handling",
        p: [
          "Wash the car so damage is visible in photos. Remove all personal items, toll transponders, and parking passes. Leave about a quarter tank of fuel. Disable the alarm or brief the driver. Note any leaks or quirks. Photograph every panel, the roof, the wheels, and the interior with timestamps.",
          "At pickup and delivery, walk the vehicle with the driver and make sure the bill of lading matches reality before you sign. Signing a clean bill of lading at delivery closes the door on most damage claims.",
        ],
      },
      {
        h: "Request a luxury car shipping quote",
        p: [
          "Coast-to-coast luxury car transport requires accurate pricing, verified insurance, and a coordinator who understands high-value vehicles. Use our Get a Quote form to receive a dedicated enclosed quote for your luxury car move.",
        ],
      },
    ],
    takeaways: [
      "Luxury cars should almost always ship enclosed.",
      "Verify the carrier's MC number, insurance, and safety record before dispatch.",
      "Document the vehicle's condition thoroughly at pickup and delivery.",
    ],
  },
  {
    slug: "enclosed-transport-classic-cars",
    title: "Why Enclosed Auto Transport Is Worth It for Classic Cars",
    excerpt:
      "Classic cars need more than mileage protection. Enclosed shipping shields irreplaceable paint, chrome, and trim from the hazards open carriers cannot avoid.",
    category: "Specialty Vehicles",
    date: "2026-08-18",
    readMinutes: 6,
    sections: [
      {
        h: "Classic cars are not replaceable",
        p: [
          "A classic car is usually worth more than its market price. The paint may be decades old, the chrome may be original, and the body panels may no longer be manufactured. A stone chip on a modern car is annoying. A stone chip on a numbers-matching classic can be a permanent flaw.",
          "That is why collectors treat enclosed transport as standard, not optional. The sealed trailer keeps weather, dust, and debris away, and the lift gate prevents the undercarriage scrapes that happen on steep ramps.",
        ],
      },
      {
        h: "Soft-tie systems protect fragile suspension",
        p: [
          "Older suspensions and original bushings do not respond well to chains yanked tight through the control arms. Soft-tie wheel straps loop over the tires and hold the wheels to the deck, leaving suspension travel intact and keeping tension off body panels.",
          "Every enclosed load we dispatch uses soft-tie securing. If a carrier wants to chain your classic car down, that is a sign to keep looking.",
        ],
      },
      {
        h: "Insurance that matches the value",
        p: [
          "Many classic cars are insured for agreed value, but the carrier's cargo policy is what pays if damage happens in transit. Open carriers often carry $250,000 in coverage, which may not be enough for a high-end collector car. Enclosed carriers typically carry $1,000,000, giving both the owner and the carrier room to handle a claim properly.",
        ],
      },
      {
        h: "Book enclosed transport for your classic",
        p: [
          "Whether you are heading to a show, selling to a buyer across the country, or moving your collection south for the winter, enclosed transport is the safest way to move a classic car. Get a quote through our Get a Quote form and we will match your vehicle with a carrier experienced in collector cars.",
        ],
      },
    ],
    takeaways: [
      "Classic cars should ship enclosed because damage is often irreplaceable.",
      "Soft-tie wheel straps protect older suspensions and body panels.",
      "Enclosed carriers typically carry $1,000,000 in cargo coverage.",
    ],
  },
  {
    slug: "what-is-white-glove-auto-transport",
    title: "What Is White Glove Auto Transport and Do You Need It?",
    excerpt:
      "White glove auto transport goes beyond enclosed shipping with extra coordination, handling, and communication. Here is what you actually get for the premium.",
    category: "Shipping Types",
    date: "2026-08-18",
    readMinutes: 6,
    sections: [
      {
        h: "White glove is service, not just equipment",
        p: [
          "White glove auto transport starts with enclosed equipment but adds a layer of coordination. That can mean single-vehicle trailers, top-load placement, extra padding, covered interior protection, guaranteed pickup windows, and direct owner-to-coordinator communication throughout the move.",
          "It is designed for owners who do not want to worry about the details — people shipping one-of-one vehicles, auction purchases, or cars that are part of a larger relocation where timing matters.",
        ],
      },
      {
        h: "What white glove typically includes",
        p: [
          "The exact package varies by broker and carrier, but white glove service usually covers the following.",
        ],
        list: [
          "Enclosed trailer with hard walls and roof",
          "Hydraulic lift-gate loading",
          "Soft-tie wheel straps and padded decking",
          "Top-load placement when possible",
          "Direct coordinator contact and status updates",
          "Pickup and delivery windows that match your schedule",
          "Assistance with bill of lading documentation and photo records",
        ],
      },
      {
        h: "Do you need white glove service?",
        p: [
          "If your vehicle is a daily driver, standard enclosed transport is usually enough. White glove makes sense when the car is irreplaceable, the move is complex, or you simply want someone else to manage every detail. Owners of exotic collections, vintage race cars, and show vehicles are the most common customers.",
          "The premium is real, but so is the peace of mind. You are paying for attention, not just trailer walls.",
        ],
      },
      {
        h: "Ask about white glove options",
        p: [
          "Not every shipment needs white glove handling, but if you think yours might, ask when you request your quote. Use our Get a Quote form and mention that you are interested in white glove or single-vehicle enclosed service. We will confirm what is available for your lane and dates.",
        ],
      },
    ],
    takeaways: [
      "White glove transport adds coordination and handling to enclosed shipping.",
      "It is best for irreplaceable, complex, or time-sensitive moves.",
      "Standard enclosed transport is enough for most luxury vehicles.",
    ],
  },
];

export function getPost(slug: string) {
  return BLOG_POSTS.find((post) => post.slug === slug);
}

export function formatPostDate(date: string) {
  return new Date(`${date}T12:00:00Z`).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}
